import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import {
  Button,
  Icon,
  USER_INTERFACE_ARROW_04_LEFT,
} from '@bwoty-web/styleguide';
import {
  activeFilterStepPropType,
  filtersPropType,
} from '../../utils/propTypes';
import { logEventToDataLayer } from '../../utils/tracking';
import {
  baseRoutes,
  filterSteps,
  filterStepsTypes,
  destinationTraits,
  destinationTraitNames,
} from '../../appSettings';
import './filterFlowNavigation.scss';

const FilterFlowNavigation = ({
  activeFilterStep,
  filters,
  totalHits,
  labels,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNextFilterStep = () => {
    logEventToDataLayer({
      filterSteps,
      filterStepsTypes,
      filters,
      activeFilterStep,
      destinationTraits,
      destinationTraitNames,
      eventActionClickType: 'Next',
    });

    const nextStepItem = filterSteps[activeFilterStep.index + 1];
    if (nextStepItem) {
      navigate(nextStepItem.id);
    } else {
      // final page of the filter-steps. Go to the congrats page
      navigate(`/${baseRoutes.CONGRATULATIONS}`);
    }
  };

  const handlePreviousFilterStep = () => {
    logEventToDataLayer({
      filterSteps,
      filterStepsTypes,
      filters,
      activeFilterStep,
      destinationTraits,
      destinationTraitNames,
      eventActionClickType: 'Previous',
    });
    const previousStepItem = filterSteps[activeFilterStep.index - 1];
    if (previousStepItem) {
      navigate(previousStepItem.id);
    } else {
      // previous page is not one of the filter-step pages
      navigate('/');
    }
  };

  const primaryClassificationValidation = () => {
    return filters.selectedPrimaryClassifications.length > 0
      ? false
      : 'disabled';
  };

  const secondaryClassificationValidation = () => {
    return filters.selectedSecondaryClassifications.length > 0
      ? false
      : 'disabled';
  };

  const getButtonActiveState = () => {
    // If the user picks filters that results in 0 hits -> Don't allow the user to go to the next step
    if (totalHits === 0) {
      return true; // disabled
    }

    const currentRoute = location.pathname;
    switch (currentRoute) {
      case `/${filterStepsTypes.PRIMARY_CLASSIFICATIONS}`:
        return primaryClassificationValidation();
      case `/${filterStepsTypes.SECONDARY_CLASSIFICATIONS}`:
        return secondaryClassificationValidation();
      default:
        return false;
    }
  };

  const showNumberOfHits = () => {
    const stepsToHide = [filterStepsTypes.PRIMARY_CLASSIFICATIONS];
    return !stepsToHide.includes(activeFilterStep.id);
  };

  const showResults = () => {
    logEventToDataLayer({
      filterSteps,
      filterStepsTypes,
      filters,
      activeFilterStep,
      destinationTraits,
      destinationTraitNames,
      eventActionClickType: 'Show Results',
    });
    window.location.href = `${window.location.origin}${labels.resultListUrl}/${window.location.search}#/`;
  };

  const getNextButtonActiveStateDataTest = () => {
    if (!getButtonActiveState()) {
      return '';
    }

    return 'disabled';
  };

  const buttonsDisabled = getButtonActiveState();
  const resultButtonClasses = classnames({
    'vf-results-button': true,
    'vf-results-button--disabled': buttonsDisabled,
  });
  const nextButtonClasses = classnames({
    'vf-next-button': true,
    'vf-next-button--disabled': buttonsDisabled,
  });

  return (
    <section className="filter-step-nav-button-container">
      <button
        type="button"
        className="vf-back-button-round"
        data-test="back-button"
        onClick={handlePreviousFilterStep}
      >
        <Icon
          fill="rgb(255, 94, 0)"
          className="vf-back-button-round-icon"
          path={USER_INTERFACE_ARROW_04_LEFT}
          size="xs"
        />
      </button>

      {showNumberOfHits() && (
        <Button
          onClick={() => showResults()}
          className={resultButtonClasses}
          data-test="results-button"
          disabled={buttonsDisabled}
        >
          {totalHits === 0
            ? labels.filterFlowNavNoHits
            : `${labels.filterFooterSeeResults1} ${totalHits || '-'} ${
                labels.filterFooterSeeResults2
              }`}
        </Button>
      )}

      <Button
        theme="primary"
        disabled={buttonsDisabled}
        className={nextButtonClasses}
        data-test={`next-button-${getNextButtonActiveStateDataTest()}`}
        onClick={handleNextFilterStep}
      >
        {labels.filterFooterNext}
      </Button>
    </section>
  );
};

FilterFlowNavigation.defaultProps = {
  totalHits: null,
};

FilterFlowNavigation.propTypes = {
  activeFilterStep: PropTypes.shape(activeFilterStepPropType).isRequired,
  labels: PropTypes.shape({
    filterFooterNext: PropTypes.string.isRequired,
    filterFooterSeeResults1: PropTypes.string.isRequired,
    filterFooterSeeResults2: PropTypes.string.isRequired,
    resultListUrl: PropTypes.string.isRequired,
    filterFlowNavNoHits: PropTypes.string.isRequired,
  }).isRequired,
  filters: PropTypes.shape(filtersPropType).isRequired,
  totalHits: PropTypes.number,
};

export default FilterFlowNavigation;
